/**
 * speed-controller.ts — single source of truth for game speed (v2M3).
 *
 * Two pause sources combine into the effective sim speed:
 *   - userPaused — toggled by Space or by clicking Pause in the HUD widget.
 *   - menuPaused — set while the in-game Esc menu is open (and any future
 *     full-screen overlay that pauses single-player per UI_UX §2.3).
 *
 * Plus a userSpeed of 1 / 2 / 4 (per UI_UX §1.1 + §5).
 *
 *   effective = (menuPaused || userPaused) ? 0 : userSpeed
 *
 * The controller takes a `setSpeed` callback rather than a SimLoopHandle
 * directly, so tests can spy on the dispatched values without spinning up a
 * real loop. Production wiring passes `simLoop.setSpeed`.
 *
 * Replaces the ad-hoc `speedBeforePause` snapshot in session.ts (v2M1.5):
 * with two independent pause sources, snapshot/restore would tangle —
 * declarative recompute is cleaner.
 */

export type UserSpeed = 1 | 2 | 4;

export const USER_SPEED_DEFAULT: UserSpeed = 1;
export const USER_SPEED_LADDER: readonly UserSpeed[] = [1, 2, 4];

export interface SpeedController {
  /** Current player-chosen speed (independent of pause state). */
  getUserSpeed(): UserSpeed;
  /** True when the player has paused via Space or the Pause button. */
  isUserPaused(): boolean;
  /** True when an overlay (Esc menu) has paused single-player play. */
  isMenuPaused(): boolean;
  /** Convenience: paused for any reason (used by HUD highlight). */
  isPaused(): boolean;

  /** Set the player-chosen speed. Implicitly clears userPaused — picking 2× resumes if paused. */
  setUserSpeed(s: UserSpeed): void;
  /** Toggle player-pause. Doesn't touch menuPaused. */
  toggleUserPause(): void;
  /** Step userSpeed up the ladder, clamping at the top. Implicitly clears userPaused. */
  cycleSpeedUp(): void;
  /** Step userSpeed down the ladder, clamping at the bottom. Implicitly clears userPaused. */
  cycleSpeedDown(): void;

  /** Open the Esc menu (or other pausing overlay). Sim drops to 0 until resume. */
  pauseForMenu(): void;
  /** Close the Esc menu. Restores effective speed (which may be 0 if userPaused is also set). */
  resumeFromMenu(): void;
}

export interface SpeedControllerOpts {
  /**
   * Called whenever the effective sim speed changes. Production passes
   * `simLoop.setSpeed`; tests pass a spy.
   */
  setSpeed: (effective: number) => void;
  /** Initial player speed. Defaults to USER_SPEED_DEFAULT (1×). */
  initialSpeed?: UserSpeed;
}

export function createSpeedController(opts: SpeedControllerOpts): SpeedController {
  const { setSpeed } = opts;
  let userSpeed: UserSpeed = opts.initialSpeed ?? USER_SPEED_DEFAULT;
  let userPaused = false;
  let menuPaused = false;

  function applySpeed(): void {
    setSpeed(menuPaused || userPaused ? 0 : userSpeed);
  }

  // Push the initial value through so the sim loop reflects state from
  // the moment the controller is created. Otherwise a fresh controller +
  // an existing sim loop could disagree until the first user action.
  applySpeed();

  return {
    getUserSpeed: () => userSpeed,
    isUserPaused: () => userPaused,
    isMenuPaused: () => menuPaused,
    isPaused: () => userPaused || menuPaused,

    setUserSpeed(s: UserSpeed): void {
      userSpeed = s;
      // Picking a speed implicitly resumes — matches player intent (clicking
      // "2×" while paused should run at 2×, not stay paused with 2× as the
      // resume-target).
      userPaused = false;
      applySpeed();
    },
    toggleUserPause(): void {
      userPaused = !userPaused;
      applySpeed();
    },
    cycleSpeedUp(): void {
      const idx = USER_SPEED_LADDER.indexOf(userSpeed);
      if (idx < USER_SPEED_LADDER.length - 1) {
        userSpeed = USER_SPEED_LADDER[idx + 1]!;
      }
      // Cycling implicitly resumes — same reasoning as setUserSpeed.
      userPaused = false;
      applySpeed();
    },
    cycleSpeedDown(): void {
      const idx = USER_SPEED_LADDER.indexOf(userSpeed);
      if (idx > 0) {
        userSpeed = USER_SPEED_LADDER[idx - 1]!;
      }
      userPaused = false;
      applySpeed();
    },

    pauseForMenu(): void {
      menuPaused = true;
      applySpeed();
    },
    resumeFromMenu(): void {
      menuPaused = false;
      applySpeed();
    },
  };
}
